import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { ContentCopy, DeleteOutline } from '@mui/icons-material'
import { ProfileLayout } from '@/components/layout/ProfileLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { mcpConnectionsApi, type McpConnection } from '@/api/mcpConnections'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

/** Адрес MCP-сервера учителя: строится из базового URL API. */
function teacherServerUrl(): string {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4545/api'

  return `${apiUrl.replace(/\/api\/?$/, '')}/mcp/teacher`
}

/**
 * Подключённые AI-приложения.
 *
 * Секретов на странице нет: подключение идёт через браузер по OAuth, токен
 * пользователь не видит и никуда не копирует.
 */
export function ProfileConnectionsPage() {
  const { t, i18n } = useTranslation()
  const [connections, setConnections] = useState<McpConnection[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [pendingRevoke, setPendingRevoke] = useState<McpConnection | null>(null)
  const [isRevoking, setIsRevoking] = useState(false)

  const serverUrl = teacherServerUrl()

  const load = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      setConnections(await mcpConnectionsApi.getList())
    } catch (err: unknown) {
      setError(displayErrorMessage(getErrorMessage(err), t('mcpConnections.loadError')))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const revoke = async () => {
    if (pendingRevoke === null) return

    try {
      setIsRevoking(true)
      await mcpConnectionsApi.revoke(pendingRevoke.id)
      setSuccess(t('mcpConnections.revoked'))
      setPendingRevoke(null)
      await load()
    } catch (err: unknown) {
      setError(displayErrorMessage(getErrorMessage(err), t('mcpConnections.revokeError')))
    } finally {
      setIsRevoking(false)
    }
  }

  const copyServerUrl = () => {
    void navigator.clipboard?.writeText(serverUrl).then(
      () => setSuccess(t('mcpConnections.urlCopied')),
      () => setError(t('mcpConnections.copyError')),
    )
  }

  const formatDate = (value: string | null): string =>
    value === null ? '—' : new Date(value).toLocaleString(i18n.language)

  const visible = connections.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <ProfileLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('mcpConnections.title')}
          </Typography>
          <PageHelpPopover pageKey="profile-connections" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('mcpConnections.subtitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('mcpConnections.howToTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('mcpConnections.howToText')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Paper variant="outlined" sx={{ px: 1.5, py: 1, fontFamily: 'monospace' }}>
                {serverUrl}
              </Paper>
              <Tooltip title={t('mcpConnections.copyUrl')}>
                <IconButton onClick={copyServerUrl} aria-label={t('mcpConnections.copyUrl')}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            {isLoading ? (
              <Box>
                {[0, 1, 2].map((row) => (
                  <Skeleton key={row} height={48} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : connections.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t('mcpConnections.emptyTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('mcpConnections.emptyText')}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('mcpConnections.columns.client')}</TableCell>
                        <TableCell>{t('mcpConnections.columns.connectedAt')}</TableCell>
                        <TableCell>{t('mcpConnections.columns.lastUsedAt')}</TableCell>
                        <TableCell>{t('mcpConnections.columns.expiresAt')}</TableCell>
                        <TableCell align="right">{t('mcpConnections.columns.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visible.map((connection) => (
                        <TableRow key={connection.id}>
                          <TableCell>{connection.client_name ?? '—'}</TableCell>
                          <TableCell>{formatDate(connection.connected_at)}</TableCell>
                          <TableCell>{formatDate(connection.last_used_at)}</TableCell>
                          <TableCell>{formatDate(connection.expires_at)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title={t('mcpConnections.revoke')}>
                              <IconButton
                                color="error"
                                onClick={() => setPendingRevoke(connection)}
                                aria-label={t('mcpConnections.revoke')}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={connections.length}
                  page={page}
                  onPageChange={(_, next) => setPage(next)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10))
                    setPage(0)
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                  labelRowsPerPage={t('mcpConnections.rowsPerPage')}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to} ${t('mcpConnections.of')} ${count}`
                  }
                />
              </>
            )}
          </CardContent>
        </Card>

        <Dialog open={pendingRevoke !== null} onClose={() => setPendingRevoke(null)}>
          <DialogTitle>{t('mcpConnections.revokeConfirmTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('mcpConnections.revokeConfirmText', {
                client: pendingRevoke?.client_name ?? '—',
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingRevoke(null)} disabled={isRevoking}>
              {t('common.cancel')}
            </Button>
            <Button color="error" variant="contained" onClick={revoke} disabled={isRevoking}>
              {t('mcpConnections.revoke')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ProfileLayout>
  )
}
